import React, { useEffect, useState } from 'react';
import { Text, TextInput, TouchableOpacity, View, StyleSheet } from 'react-native';
import ColorPicker from 'react-native-wheel-color-picker';

type Props = {
  value: string;
  onChange: (hex: string) => void;
  label?: string;
  presets?: string[];
  defaultValue?: string;
  inputWidth?: number;
  wheelWidth?: number;
  wheelHeight?: number;
};

const DEFAULT_PRESETS = [
  '#ef4444', // red
  '#f59e0b', // amber
  '#eab308', // yellow
  '#10b981', // emerald
  '#14b8a6', // teal
  '#2e96e1', // blue
  '#6366f1', // indigo
  '#a855f7', // purple
  '#ec4899', // pink
  '#6b7280', // gray
  '#111827', // near-black
  '#ffffff', // white
];

// Soft pastel palette — for Plantations (used as card BACKGROUND).
// Low saturation / high lightness so text and category dots remain legible on top.
export const PLANTATION_PRESETS = [
  '#fee2e2', // red-100
  '#fed7aa', // orange-200
  '#fef3c7', // amber-100
  '#d9f99d', // lime-200
  '#bbf7d0', // green-200
  '#a7f3d0', // emerald-200
  '#99f6e4', // teal-200
  '#bae6fd', // sky-200
  '#c7d2fe', // indigo-200
  '#e9d5ff', // purple-200
  '#fbcfe8', // pink-200
  '#e7e5e4', // stone-200
];

// Bold saturated palette — for Product Categories (used as a STRIP / DOT on the card).
// All tones are 600-800 range so they contrast against any pastel from PLANTATION_PRESETS.
export const PRODUCT_CATEGORY_PRESETS = [
  '#dc2626', // red-600
  '#ea580c', // orange-600
  '#ca8a04', // yellow-600
  '#65a30d', // lime-600
  '#16a34a', // green-600
  '#0d9488', // teal-600
  '#0284c7', // sky-600
  '#4f46e5', // indigo-600
  '#9333ea', // purple-600
  '#db2777', // pink-600
  '#475569', // slate-600
  '#1f2937', // gray-800
];

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export const DEFAULT_HEX_COLOR = '#10b981';

const CompactColorPicker: React.FC<Props> = ({
  value,
  onChange,
  label = 'Color',
  presets = DEFAULT_PRESETS,
  defaultValue = DEFAULT_HEX_COLOR,
  inputWidth = 140,
  wheelWidth = 260,
  wheelHeight = 300,
}) => {
  const current = value || defaultValue;
  const [hexInput, setHexInput] = useState<string>(current);
  const [showWheel, setShowWheel] = useState<boolean>(false);

  useEffect(() => {
    setHexInput(current);
  }, [current]);

  const commitHex = () => {
    if (HEX_RE.test(hexInput)) {
      onChange(hexInput);
    } else {
      setHexInput(current);
    }
  };

  return (
    <View style={styles.container}>
      <Text style={styles.label}>{label}</Text>
      <View style={styles.inputWrap}>
        <View
          style={[styles.previewDot, { backgroundColor: current }]}
        />
        <TextInput
          style={styles.hexInput}
          value={hexInput}
          onChangeText={setHexInput}
          onBlur={commitHex}
          onSubmitEditing={commitHex}
          placeholder="#RRGGBB"
          placeholderTextColor="#ccc"
          autoCapitalize="none"
          autoCorrect={false}
        />
        <TouchableOpacity
          onPress={() => setShowWheel((s) => !s)}
          style={styles.customBtn}
        >
          <Text style={styles.customBtnText}>
            {showWheel ? 'Hide' : 'Custom'}
          </Text>
        </TouchableOpacity>
      </View>

      <View style={styles.presetsRow}>
        {presets.map((c) => {
          const selected = c.toLowerCase() === (current || '').toLowerCase();
          return (
            <TouchableOpacity
              key={c}
              onPress={() => onChange(c)}
              style={[
                styles.presetSwatch,
                { backgroundColor: c },
                selected && styles.presetSwatchSelected,
              ]}
            />
          );
        })}
      </View>

      {showWheel && (
        <View style={{ width: wheelWidth, height: wheelHeight, marginTop: 8 }}>
          <ColorPicker
            color={current}
            onColorChange={(color) => onChange(color)}
            onColorChangeComplete={() => {}}
            thumbSize={24}
            sliderSize={24}
            noSnap={true}
            row={false}
            swatches={false}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 6,
  },
  label: {
    color: '#555',
    fontSize: 13,
    marginBottom: 4,
  },
  inputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'gray',
    paddingHorizontal: 8,
    height: 40,
  },
  previewDot: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 1,
    borderColor: '#ccc',
    marginRight: 8,
  },
  hexInput: {
    flex: 1,
    paddingVertical: 0,
    paddingHorizontal: 0,
    borderWidth: 0,
    // @ts-ignore - web only
    outlineStyle: 'none',
  },
  customBtn: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#2e96e1',
    borderRadius: 4,
    marginLeft: 8,
  },
  customBtnText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
  },
  presetsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 8,
  },
  presetSwatch: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 1,
    borderColor: '#ccc',
    marginRight: 6,
    marginBottom: 6,
  },
  presetSwatchSelected: {
    borderWidth: 2,
    borderColor: '#111827',
  },
});

export default CompactColorPicker;
